import { useEffect, useMemo, useState } from "react";
import { host } from "../host.js";

export default function LensCard({ lens, favorite, active, busy, compact = false, onApply, onFavorite }) {
  const images = useMemo(() => [...new Set([
    ...(Array.isArray(lens.mediaProxyUrls) ? lens.mediaProxyUrls : []),
    lens.previewProxyUrl,
    lens.iconProxyUrl,
  ].filter(Boolean))], [lens.iconProxyUrl, lens.mediaProxyUrls, lens.previewProxyUrl]);
  const [imageState, setImageState] = useState({ status: "loading", src: "" });
  const [copyStatus, setCopyStatus] = useState("");
  useEffect(() => {
    let cancelled = false;
    let probe = null;
    let index = 0;
    setImageState({ status: images.length ? "loading" : "missing", src: "" });

    const tryNext = () => {
      if (cancelled) return;
      const src = images[index++];
      if (!src) {
        setImageState({ status: "missing", src: "" });
        return;
      }
      probe = new Image();
      probe.decoding = "async";
      probe.onload = async () => {
        try { await probe.decode?.(); } catch { /* onload still confirms a renderable image */ }
        if (!cancelled) setImageState({ status: "ready", src });
      };
      probe.onerror = tryNext;
      probe.src = src;
    };

    tryNext();
    return () => {
      cancelled = true;
      if (probe) { probe.onload = null; probe.onerror = null; }
    };
  }, [images, lens.lensId]);
  const apply = () => { if (!busy) onApply?.(lens); };
  const copyId = async () => {
    try {
      await host.copyText(String(lens.lensId));
      setCopyStatus("copied");
    } catch {
      setCopyStatus("failed");
    }
    window.setTimeout(() => setCopyStatus(""), 1400);
  };

  return (
    <article className={`lens-card${compact ? " compact" : ""}${active ? " active" : ""}${busy ? " busy" : ""}`}>
      <button className="lens-card-apply" type="button" onClick={apply} disabled={busy} aria-label={`Load ${lens.name}`}>
        <span className="lens-card-media">
          {imageState.status === "ready"
            ? (
              <img
                src={imageState.src}
                alt=""
                loading="lazy"
              />
            )
            : (
              <span className={`lens-card-fallback ${imageState.status}`} aria-hidden="true">
                {imageState.status === "missing"
                  ? <span>{lens.name.trim().slice(0, 1).toUpperCase() || "L"}</span>
                  : <span className="lens-card-skeleton" />}
              </span>
            )}
          {active && <span className="lens-card-active">Active</span>}
          {busy && <span className="lens-card-loading">Loading…</span>}
        </span>
        <span className="lens-card-copy">
          <strong title={lens.name}>{lens.name}</strong>
          <span title={lens.creatorName}>{lens.creatorName}</span>
        </span>
      </button>
      <button
        className={`lens-card-star${favorite ? " selected" : ""}`}
        type="button"
        aria-label={favorite ? `Remove ${lens.name} from favorites` : `Add ${lens.name} to favorites`}
        aria-pressed={favorite}
        onClick={() => onFavorite?.(lens)}
      >
        {favorite ? "★" : "☆"}
      </button>
      <button
        className="lens-card-id"
        type="button"
        aria-label={`Copy lens ID ${lens.lensId}`}
        title={`Copy lens ID ${lens.lensId}`}
        onClick={copyId}
      >
        <span>ID</span>
        <svg className="lens-card-copy-icon" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M5.5 4.5V3.25c0-.69.56-1.25 1.25-1.25h6c.69 0 1.25.56 1.25 1.25v6c0 .69-.56 1.25-1.25 1.25H11.5" />
          <rect x="2" y="5.5" width="8.5" height="8.5" rx="1.25" />
        </svg>
        {copyStatus && (
          <span className={`lens-card-copy-tooltip ${copyStatus}`} role="status">
            {copyStatus === "copied" ? "Copied" : "Copy failed"}
          </span>
        )}
      </button>
    </article>
  );
}
